import { Link } from 'react-router-dom';
import { QrCode, ExternalLink, Twitter, Linkedin, Instagram, Youtube, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function Footer() {
  const handleOpenCookieSettings = () => {
    // Dispatch custom event to open cookie consent banner
    window.dispatchEvent(new CustomEvent('open-cookie-settings'));
  };

  const footerSections = [
    {
      title: 'Product',
      links: [
        { label: 'Create QR Code', href: '/create' },
        { label: 'Templates', href: '/templates' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Analytics', href: '/analytics' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Shipping Policy', href: '/shipping' },
        { label: 'Cancellations & Refunds', href: '/refunds' },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Contact Us', href: '/contact' },
        { label: 'Settings', href: '/settings' },
        { label: 'Team Workspace', href: '/workspace' },
        { label: 'System Status', href: '/status' },
      ],
    },
  ];

  const socials = [
    { icon: Twitter, href: 'https://twitter.com/nexqr', label: 'Twitter' },
    { icon: Linkedin, href: 'https://linkedin.com/company/nexqr', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://www.instagram.com/nexqr.online/', label: 'Instagram' },
    { icon: Youtube, href: 'https://youtube.com/@nexqr', label: 'YouTube' },
    { icon: Facebook, href: 'https://facebook.com/nexqr', label: 'Facebook' },
  ];

  return (
    <footer className="border-t border-border/60 bg-muted/20">
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 md:gap-8">
          {/* Brand */}
          <div className="col-span-2 space-y-4">
            <Link to="/" className="inline-block">
              <img 
                src="/logo.png" 
                alt="NexQR Logo" 
                width="120" 
                height="32" 
                loading="lazy"
                decoding="async"
                className="h-7 w-auto object-contain" 
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              Create beautiful, trackable QR codes with dynamic content, real-time analytics, and enterprise security.
            </p>
            <div className="flex items-center gap-2 pt-2">
              {socials.map(({ icon: Icon, href, label }) => (
                <a 
                  key={label}
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label={`Follow us on ${label}`} 
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link Sections */}
          {footerSections.map((section) => (
            <div key={section.title} className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{section.title}</h3>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-foreground/70 hover:text-foreground transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
                {section.title === 'Legal' && (
                  <li>
                    <button
                      onClick={handleOpenCookieSettings}
                      className="text-sm text-foreground/70 hover:text-foreground transition-colors text-left"
                    >
                      Cookie Settings
                    </button>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/40 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} NexQR. All rights reserved.
          </p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              All systems operational
            </span>
            <span className="text-border">|</span>
            <span>Secured by Stripe & Razorpay</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
